import React, { useEffect, useRef } from 'react'
import { createConfetti, isSettled, LYING_FLATNESS, scatterConfetti, stepConfetti, type ConfettiField } from './confettiPhysics'
import type { ConfettiShape } from './confettiShapes'
import './DoneMomentConfetti.scss'

export interface DoneMomentConfettiProps {
  /** Ab jetzt fällt es — die Karte meldet den Beginn ihres Auftritts. */
  started: boolean
  /** Wie viele Stücke. Mit Belohnung 70, ohne die Hälfte. */
  count: number
  /** Fester Startwert für den Zufall (Stories, Tests). Ohne: jedes Mal anders. */
  seed?: number
}

/**
 * Wie hoch der Haufen werden darf — unter „Weiter geht's" sind 24px Luft, und
 * der Knopf soll frei und klickbar bleiben.
 */
const MAX_PILE = 18

/**
 * Längster Schritt, den die Rechnung auf einmal nimmt (ms). Kommt das Fenster
 * nach einer Pause zurück (verstecktes Hauptfenster), springt das Konfetti
 * sonst in einem Satz nach unten.
 */
const MAX_STEP = 50

const paths = new Map<ConfettiShape, Path2D>()
const pathOf = (shape: ConfettiShape) => {
  let path = paths.get(shape)
  if (!path) {
    path = new Path2D(shape.d)
    paths.set(shape, path)
  }
  return path
}

function draw(ctx: CanvasRenderingContext2D, field: ConfettiField) {
  ctx.clearRect(0, 0, field.width, field.height)
  for (const piece of field.pieces) {
    if (piece.state === 'waiting' || piece.state === 'gone') continue
    const { shape } = piece
    // Das Kippen staucht die Breite; ganz flach verschwände das Stück.
    const flip = Math.cos(piece.tilt)
    const squash = Math.sign(flip || 1) * Math.max(0.12, Math.abs(flip))

    ctx.save()
    ctx.globalAlpha = piece.opacity
    ctx.translate(piece.x, piece.y)
    // Liegend flach: gestaucht in der Höhe des Bildschirms, nicht des Stücks.
    ctx.scale(1, 1 - (1 - LYING_FLATNESS) * piece.flat)
    ctx.rotate(piece.angle)
    ctx.scale(squash * piece.scale, piece.scale)
    ctx.translate(-shape.width / 2, -shape.height / 2)
    if (shape.strokeWidth) {
      ctx.lineWidth = shape.strokeWidth
      ctx.lineCap = 'round'
      ctx.strokeStyle = piece.color
      ctx.stroke(pathOf(shape))
    } else {
      ctx.fillStyle = piece.color
      ctx.fill(pathOf(shape))
    }
    ctx.restore()
  }
}

/**
 * **Konfetti im Geschafft-Moment** — fällt sanft in die Karte, taumelt dabei
 * und bleibt am unteren Rand liegen, auf dem, was schon liegt. Die Rechnung
 * steht in `confettiPhysics.ts`, hier wird nur gezeichnet.
 *
 * Eine Zeichenfläche über der ganzen Karte, Klicks gehen hindurch. Die Schleife
 * läuft nur, solange sich etwas bewegt — liegt alles, kostet es nichts mehr.
 * **Reduzierte Bewegung:** ein stilles, verstreutes Bild wie in Figma.
 *
 * @example
 * <DoneMomentConfetti started={cardHasRisen} count={reward ? 70 : 35} />
 */
export const DoneMomentConfetti: React.FC<DoneMomentConfettiProps> = ({ started, count, seed }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    if (!started && !reduced) return

    // Die Layout-Größe, nicht `getBoundingClientRect`: Beim Start steigt die
    // Karte gerade auf und ist noch verkleinert — der Boden säße sonst zu hoch.
    const width = canvas.clientWidth
    const height = canvas.clientHeight
    if (width <= 0 || height <= 0) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const ratio = window.devicePixelRatio || 1
    canvas.width = Math.round(width * ratio)
    canvas.height = Math.round(height * ratio)
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    const startValue = seed ?? Math.floor(Math.random() * 2 ** 31)

    if (reduced) {
      draw(ctx, scatterConfetti({ width, height, count: Math.round(count / 3), seed: startValue }))
      return
    }

    const field = createConfetti({ width, height, count, seed: startValue, maxPile: MAX_PILE })
    let last = performance.now()
    let frame = 0
    const tick = (now: number) => {
      // Der Zeitstempel eines Bilds kann vor `last` liegen — rückwärts rechnet die Welt nicht.
      stepConfetti(field, Math.max(0, Math.min(MAX_STEP, now - last)))
      last = now
      draw(ctx, field)
      if (!isSettled(field)) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [started, count, seed])

  return <canvas ref={canvasRef} className="done-moment-confetti" aria-hidden="true" />
}
